import React, { useEffect, useMemo, useState } from "react";
import { X, Home, Droplet, Zap, Wifi, Trash2, Calendar, Building2, } from "lucide-react";
import { useAddMeterReadingsMutation, useUpdateMeterReadingsMutation } from "../../lib/apiSlice/property/propertySlice.tsx";
import { toast } from "react-toastify";

const localToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
};

const meterReadingError = (error, fallback) => {
  const data = error?.data ?? error?.error?.data ?? error;
  if (typeof data === "string") return data;
  if (typeof data?.detail === "string") return data.detail;

  if (data && typeof data === "object") {
    const [field, messages] = Object.entries(data)[0] || [];
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (typeof message === "string") {
      return field && field !== "non_field_errors" ? `${field}: ${message}` : message;
    }
  }
  return fallback;
};

const AddMeterReadingModal = ({ isOpen, onClose, apartments = [], editReading = null, onSaved }) => {
  const [selectedProperty, setSelectedProperty] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedExpenseId, setSelectedExpenseId] = useState("");
  const [readingDate, setReadingDate] = useState("");
  const [meterReading, setMeterReading] = useState("");

  const selectedApartment = useMemo(
    () => apartments.find(
      (apt) => String(apt.apartment_id) === selectedProperty
    ),
    [apartments, selectedProperty]
  );

  const varyingExpenses = useMemo(
    () => selectedApartment?.expenses?.filter(
      (exp) => String(exp.expense_type || "").trim().toLowerCase() === "varying"
    ) || [],
    [selectedApartment]
  );

  const units = selectedApartment?.units || [];

  const [saveReadings, { isLoading: isSavingReading }] = useAddMeterReadingsMutation()
  const [updateReadings, { isLoading: isUpdatingReading }] = useUpdateMeterReadingsMutation()
  const isSaving = isSavingReading || isUpdatingReading;

  useEffect(() => {
    if (!isOpen) return;

    if (editReading) {
      setSelectedProperty(String(editReading.apartment_id || ""));
      setSelectedUnit(String(editReading.house_id || ""));
      setSelectedExpenseId(String(editReading.expense_id || ""));
      setMeterReading(String(editReading.units_consumed ?? ""));
      if (editReading.readings_date) {
        setReadingDate(String(editReading.readings_date).slice(0, 10));
      }
    } else {
      setSelectedProperty("");
      setSelectedUnit("");
      setSelectedExpenseId("");
      setReadingDate(localToday());
      setMeterReading("");
    }
  }, [isOpen, editReading]);

  useEffect(() => {
    if (!isOpen || editReading) return;

    setSelectedExpenseId((currentExpenseId) => {
      if (varyingExpenses.length === 1) {
        return String(varyingExpenses[0].expense_id);
      }

      const isStillAvailable = varyingExpenses.some(
        (expense) => String(expense.expense_id) === currentExpenseId
      );
      return isStillAvailable ? currentExpenseId : "";
    });
  }, [isOpen, editReading, varyingExpenses]);

  async function handleSubmit(e) {
    e?.preventDefault?.();

    const unitsConsumed = Number(meterReading);
    if (!Number.isFinite(unitsConsumed) || unitsConsumed < 0) {
      toast.error("Enter a non-negative number of units consumed.");
      return;
    }

    if (editReading?.id) {
      try {
        await updateReadings({
          id: editReading.id,
          data: { units_consumed: unitsConsumed },
        }).unwrap();
        toast.success("Meter reading updated successfully!");
        onClose();
        onSaved?.();
      } catch (error) {
        toast.error(meterReadingError(error, "Failed to update meter reading. Please try again."));
      }
      return;
    }

    if (!selectedProperty || !selectedUnit || !selectedExpenseId || !readingDate) {
      toast.error("Select a property, unit, varying utility, and reading date.");
      return;
    }

    const selectedExpense = varyingExpenses.find(
      (exp) => String(exp.expense_id) === String(selectedExpenseId)
    );
    if (!selectedExpense) {
      toast.error("Select a varying utility type.");
      return;
    }

    try {
      await saveReadings({
        units_consumed: unitsConsumed,
        house: Number(selectedUnit),
        expense: Number(selectedExpenseId),
        reading_date: readingDate,
      }).unwrap();
      toast.success("Meter reading added successfully!");

      setSelectedProperty("");
      setSelectedUnit("");
      setSelectedExpenseId("");
      setReadingDate(localToday());
      setMeterReading("");
      onClose();
      onSaved?.();

    } catch (error) {
      toast.error(meterReadingError(error, "Failed to add meter reading. Please try again."));
    }
  }



  if (!isOpen) return null;

  return (
    <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 p-4">
      <div className="bg-white shadow-xl rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b">
            <h3 className="font-semibold text-gray-900 text-lg">
            {editReading ? "Edit Meter Reading" : "Add New Meter Reading"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          {/* Property Select */}
          <div className="space-y-1">
            <p className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
              <Home className="w-4 h-4" /> Property
            </p>
            <select
              value={selectedProperty}
              onChange={(e) => {
                setSelectedProperty(e.target.value);
                setSelectedExpenseId("");
                setSelectedUnit("");
              }}
              disabled={Boolean(editReading)}
              className="block shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full text-sm"
              required
            >
              <option value="">Select property</option>
              {apartments.map((property) => (
                <option
                  key={property.apartment_id}
                  value={property.apartment_id}
                >
                  {property.apartment_name}
                </option>
              ))}
            </select>
          </div>

          {/* Unit Select */}
          {selectedProperty && units.length > 0 && (
            <div className="space-y-1">
              <p className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
                <Building2 className="w-4 h-4" /> Unit
              </p>
              <select
                value={selectedUnit}
                onChange={(e) => setSelectedUnit(e.target.value)}
                disabled={Boolean(editReading)}
                className="block shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full text-sm"
                required
              >
                <option value="">Search or select unit</option>
                {units.map((unit) => (
                  <option key={unit.house_id} value={unit.house_id}>
                    {unit.house_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedProperty && units.length === 0 && (
            <p className="text-red-500 text-xs">
              This property has no occupied units available for a meter reading.
            </p>
          )}

          {/* Utility Type Buttons */}
          <div className="space-y-1">
            <p className="font-semibold text-gray-600 text-sm">
              Utility Type (Varying Only)
            </p>
            {varyingExpenses.length === 0 ? (
              <p className="text-red-500 text-xs">
                No varying expenses for this property.
              </p>
            ) : (
              <div className="gap-2 grid grid-cols-3">
                {varyingExpenses.map(({ expense_id, expense_name }) => {
                  const lower = expense_name.toLowerCase();
                  const iconMap = {
                    water: <Droplet />,
                    electricity: <Zap />,
                    internet: <Wifi />,
                    garbage: <Trash2 />,
                  };

                  return (
                    <button
                      key={expense_id}
                      type="button"
                      onClick={() => setSelectedExpenseId(String(expense_id))}
                      disabled={Boolean(editReading)}
                      aria-pressed={String(selectedExpenseId) === String(expense_id)}
                      className={`p-2 rounded-md flex flex-col items-center justify-center text-sm ${String(selectedExpenseId) === String(expense_id)
                        ? "bg-blue-100 text-blue-700 border border-blue-300"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                      {React.cloneElement(iconMap[lower] || <Droplet />, {
                        className: "mb-1 w-5 h-5",
                      })}
                      {expense_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reading Date */}
          <div className="space-y-1">
            <p className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
              <Calendar className="w-4 h-4" /> Reading Date
            </p>
            <input
              type="date"
              value={readingDate}
              onChange={(e) => setReadingDate(e.target.value)}
              disabled={Boolean(editReading)}
              className="block shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full text-sm"
              required
            />
          </div>

          {/* Meter Reading */}
          <div className="space-y-1">
            <p className="font-semibold text-gray-600 text-sm">Units Consumed</p>
            <input
              type="number"
              value={meterReading}
              onChange={(e) => setMeterReading(e.target.value)}
              className="block px-3 py-2 pr-16 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full text-sm"
              placeholder="0"
              min="0"
              step="0.01"
              inputMode="decimal"
              required
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-white hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (!editReading && (!selectedProperty || !selectedUnit || !selectedExpenseId || !readingDate))}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 px-4 py-2 border border-transparent rounded-md text-white text-sm disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : editReading ? "Update Reading" : "Save Reading"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMeterReadingModal;
